// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * The model registry, for the admin analysis page: which detectors have been trained, on
 * what, and how each scores per feature kind. Read-only — the training tooling writes it.
 */

export type MlEvaluation = {
  id: string;
  kind: string;
  provider: string;
  precision: number;
  recall: number;
  f1: number;
  iou: number | null;
  matched: number;
  missed: number;
  falsePositive: number;
  evaluatedAt: string | null;
  notes: string | null;
  /** How many courses the score was measured over — one is a sample, five is a claim. */
  courseCount: number;
};

export type MlModel = {
  /** Null for the classical (threshold) detector, which has evaluations but no model row. */
  id: string | null;
  name: string;
  version: string;
  /** Two-word name for people ('amber-albatross'); null for a model registered before nicknames. */
  nickname: string | null;
  architecture: string;
  datasetId: string | null;
  datasetDescription: string | null;
  epochs: number;
  /**
   * What train.py selects the checkpoint on, stored as 1 − mIoU so the registry column's
   * lower-is-better reading holds. The page turns it back into the mIoU people quote.
   */
  bestValLoss: number | null;
  trainedAt: string | null;
  notes: string | null;
  /**
   * Everything train.py recorded about the run, as it stored it: encoder, channel count,
   * batch size, learning rate, providers, crop and course counts. Raw JSON on purpose — the
   * keys are train.py's to change, so the reader takes what it recognises.
   */
  hyperparamsJson: string | null;
  /** Set once the weights are in the shared model store; null means one machine has them. */
  checkpointS3Key: string | null;
  latestEvaluations: MlEvaluation[];
};

export type MlDataset = {
  id: string;
  createdAt: string | null;
  description: string;
  counts: Record<string, number>;
  /**
   * What the extractor selected by, as it stored it: provider, course ids, imagery source,
   * the richness floors and the seed. Kept as the raw JSON the registry holds — the shape is
   * the extractor's to change, so the reader decides what it can make of it.
   */
  filterJson: string | null;
  /**
   * Where the crops and manifest were published, under the exchange the extract job came from
   * — or null for a dataset that exists only on the machine that cut it, which no other machine
   * can train on.
   */
  manifestS3Key: string | null;
};

type Row = Record<string, unknown>;

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const mapEvaluation = (row: Row): MlEvaluation | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  if (!id || !kind) return null;
  return {
    id,
    kind,
    provider: str(row, 'provider', 'Provider') ?? '',
    precision: num(row, 'precision', 'Precision'),
    recall: num(row, 'recall', 'Recall'),
    f1: num(row, 'f1', 'F1'),
    iou: numOrNull(row, 'iou', 'Iou'),
    matched: num(row, 'matched', 'Matched'),
    missed: num(row, 'missed', 'Missed'),
    falsePositive: num(row, 'falsePositive', 'FalsePositive'),
    evaluatedAt: str(row, 'evaluatedAt', 'EvaluatedAt'),
    notes: str(row, 'notes', 'Notes'),
    courseCount: (() => {
      const ids = row.courseIds ?? row.CourseIds;
      return Array.isArray(ids) ? ids.length : 0;
    })(),
  };
};

export const mapMlModel = (row: Row): MlModel | null => {
  const name = str(row, 'name', 'Name');
  const version = str(row, 'version', 'Version');
  if (!name || !version) return null;
  const rawEvaluations = row.latestEvaluations ?? row.LatestEvaluations;
  return {
    id: str(row, 'id', 'Id'),
    name,
    version,
    nickname: str(row, 'nickname', 'Nickname'),
    architecture: str(row, 'architecture', 'Architecture') ?? '',
    datasetId: str(row, 'datasetId', 'DatasetId'),
    datasetDescription: str(row, 'datasetDescription', 'DatasetDescription'),
    epochs: num(row, 'epochs', 'Epochs'),
    bestValLoss: numOrNull(row, 'bestValLoss', 'BestValLoss'),
    trainedAt: str(row, 'trainedAt', 'TrainedAt'),
    notes: str(row, 'notes', 'Notes'),
    hyperparamsJson: str(row, 'hyperparamsJson', 'HyperparamsJson'),
    checkpointS3Key: str(row, 'checkpointS3Key', 'CheckpointS3Key'),
    latestEvaluations: (Array.isArray(rawEvaluations) ? (rawEvaluations as Row[]) : [])
      .map(mapEvaluation)
      .filter((e): e is MlEvaluation => e !== null),
  };
};

export const mapMlDataset = (row: Row): MlDataset | null => {
  const id = str(row, 'id', 'Id');
  if (!id) return null;
  const rawCounts = (row.counts ?? row.Counts) as Record<string, unknown> | undefined;
  const counts: Record<string, number> = {};
  if (rawCounts && typeof rawCounts === 'object') {
    for (const [key, value] of Object.entries(rawCounts)) {
      if (typeof value === 'number' && Number.isFinite(value)) counts[key] = value;
    }
  }
  return {
    id,
    createdAt: str(row, 'createdAt', 'CreatedAt'),
    description: str(row, 'description', 'Description') ?? '',
    counts,
    filterJson: str(row, 'filterJson', 'FilterJson'),
    manifestS3Key: str(row, 'manifestS3Key', 'ManifestS3Key'),
  };
};

export type MlModelsState = {
  models: MlModel[];
  datasets: MlDataset[];
  loading: boolean;
  error: 'forbidden' | 'unavailable' | 'failed' | null;
};

export function useMlModels(
  apiBaseUrl: string,
  token: string | null | undefined,
  enabled: boolean
): MlModelsState {
  const [models, setModels] = useState<MlModel[]>([]);
  const [datasets, setDatasets] = useState<MlDataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<MlModelsState['error']>(null);

  useEffect(() => {
    if (!enabled || !token) {
      setModels([]);
      setDatasets([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/models`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!response.ok) {
          setError(response.status === 403 ? 'forbidden' : response.status === 404 ? 'unavailable' : 'failed');
          return;
        }
        const body = (await response.json()) as Row;
        const rawModels = body.models ?? body.Models;
        const rawDatasets = body.datasets ?? body.Datasets;
        setModels(
          (Array.isArray(rawModels) ? (rawModels as Row[]) : [])
            .map(mapMlModel)
            .filter((m): m is MlModel => m !== null)
        );
        setDatasets(
          (Array.isArray(rawDatasets) ? (rawDatasets as Row[]) : [])
            .map(mapMlDataset)
            .filter((d): d is MlDataset => d !== null)
        );
      } catch {
        if (!cancelled) setError('failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled, token]);

  return { models, datasets, loading, error };
}
